const REDACTED = '[REDACTED]'

// Matches keys like password, token, accessToken, refresh_token, apiKey, secret, otp, pin — case-insensitive.
const SENSITIVE_KEY_PATTERN = /password|token|secret|authorization|api[-_]?key|otp|\bpin\b/i

/**
 * Recursively redacts values whose key looks sensitive (password, token, secret, ...).
 * Used as the last line of defense before data reaches Sentry (breadcrumbs, extra, contexts) —
 * request/response bodies logged via apiLogger otherwise carry plaintext credentials.
 */
export function redactSensitiveData<T>(
  value: T,
  seen: WeakMap<object, unknown> = new WeakMap(),
): T {
  if (value === null || typeof value !== 'object') {
    return value
  }

  // A repeated (or cyclic) reference returns the redacted copy already built — never the original.
  if (seen.has(value)) {
    return seen.get(value) as T
  }

  if (Array.isArray(value)) {
    const redactedArray: unknown[] = []
    seen.set(value, redactedArray)
    for (const item of value as unknown[]) {
      redactedArray.push(redactSensitiveData(item, seen))
    }
    return redactedArray as unknown as T
  }

  const result: Record<string, unknown> = {}
  seen.set(value, result)
  for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
    result[key] = SENSITIVE_KEY_PATTERN.test(key) ? REDACTED : redactSensitiveData(val, seen)
  }
  return result as T
}
